import React, { useState, useCallback } from 'react';
import { Card, Button, message, Empty, Spin, Tooltip } from 'antd';
import {
  PlusOutlined,
  ClearOutlined,
  ArrowsAltOutlined,
} from '@ant-design/icons';
import { useList, useDelete, useUpdate, useCreate } from '@refinedev/core';
import { ImageGrid } from './components/ImageGrid';
import { ImageUploadModal } from './components/ImageUploadModal';
import { ImageViewerModal } from './components/ImageViewerModal';
import { imageService } from '@src/services/image.service';
import type { ImageItem, ImageCategory, UploadFile } from './types';
import { sortImagesForCompareAscending } from './compareOrder';

const MAX_COMPARE_SELECTION = 6;

interface ImagesTabProps {
  patientId?: string | number;
}

export const ImagesTab: React.FC<ImagesTabProps> = ({ patientId }) => {
  const [activeCategory, setActiveCategory] =
    useState<ImageCategory>('GENERAL');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [viewerModalVisible, setViewerModalVisible] = useState(false);

  const [selectedImages, setSelectedImages] = useState<ImageItem[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState<number[]>([]);
  const [comparingImages, setComparingImages] = useState<ImageItem[]>([]);

  const currentFilters = [
    ...(patientId
      ? [
          {
            field: 'patient_id',
            operator: 'eq' as const,
            value: patientId,
          },
        ]
      : []),
    ...(activeCategory
      ? [
          {
            field: 'category',
            operator: 'eq' as const,
            value: activeCategory,
          },
        ]
      : []),
  ];

  const { data, isLoading, refetch } = useList<ImageItem>({
    resource: 'images',
    filters: currentFilters,
    sorters: [
      {
        field: 'created_at',
        order: 'desc',
      },
    ],
    pagination: {
      current: 1,
      pageSize: 100,
    },
    queryOptions: {
      enabled: !!patientId,
    },
    meta: {
      queryKey: [`images-${patientId}-${activeCategory}`],
    },
  });

  const images = data?.data || [];
  const loading = isLoading;

  const { mutate: deleteImageMutation } = useDelete();
  const { mutate: updateImageMutation } = useUpdate();
  const { mutate: createImageMutation } = useCreate();

  const deleteImage = useCallback(
    async (imageId: number) => {
      return new Promise<void>((resolve, reject) => {
        deleteImageMutation(
          {
            resource: 'images',
            id: imageId,
          },
          {
            onSuccess: () => {
              resolve();
            },
            onError: error => {
              reject(error);
            },
          }
        );
      });
    },
    [deleteImageMutation]
  );

  const updateImage = useCallback(
    async (imageId: number, data: Partial<ImageItem>) => {
      return new Promise<void>((resolve, reject) => {
        updateImageMutation(
          {
            resource: 'images',
            id: imageId,
            values: data,
          },
          {
            onSuccess: () => {
              resolve();
            },
            onError: error => {
              reject(error);
            },
          }
        );
      });
    },
    [updateImageMutation]
  );

  const uploadImages = useCallback(
    async (files: UploadFile[]): Promise<ImageItem[]> => {
      try {
        const uploadPromises = files.map(async fileData => {
          return await imageService.uploadImageDirectly(
            fileData.file,
            patientId as number,
            undefined,
            fileData.category
          );
        });

        const uploadedImages = await Promise.all(uploadPromises);

        const createPromises = uploadedImages.map(async imageData => {
          return new Promise<ImageItem>((resolve, reject) => {
            createImageMutation(
              {
                resource: 'images',
                values: imageData,
              },
              {
                onSuccess: data => {
                  resolve(data.data as ImageItem);
                },
                onError: error => {
                  reject(error);
                },
              }
            );
          });
        });

        const results = await Promise.all(createPromises);
        return results;
      } catch (error) {
        throw error;
      }
    },
    [patientId, createImageMutation]
  );

  if (!patientId) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Empty description="No patient selected" />
      </div>
    );
  }

  const handleUploadSuccess = () => {
    setUploadModalVisible(false);
    refetch();
    message.success('Images uploaded successfully');
  };

  const handleImageSelect = (imageId: number) => {
    if (compareMode) {
      const newSelection = selectedImageIds.includes(imageId)
        ? selectedImageIds.filter(id => id !== imageId)
        : selectedImageIds.length < MAX_COMPARE_SELECTION
          ? [...selectedImageIds, imageId]
          : selectedImageIds;
      setSelectedImageIds(newSelection);
    } else {
      const image = images.find((img: ImageItem) => img.id === imageId);
      if (image) {
        const index = images.findIndex((img: ImageItem) => img.id === imageId);
        setSelectedImages(images);
        setCurrentImageIndex(index);
        setViewerModalVisible(true);
      }
    }
  };

  const handleImageView = (image: ImageItem) => {
    const index = images.findIndex((img: ImageItem) => img.id === image.id);
    setSelectedImages(images);
    setCurrentImageIndex(index);
    setViewerModalVisible(true);
  };

  const handleImageDelete = async (imageId: number) => {
    try {
      await deleteImage(imageId);
      refetch();
      message.success('Image deleted successfully');
    } catch (error) {
      message.error('Failed to delete image');
    }
  };

  const handleImageUpdate = async (
    imageId: number,
    data: Partial<ImageItem>
  ) => {
    try {
      await updateImage(imageId, data);
      refetch();
      message.success('Image updated successfully');
    } catch (error) {
      message.error('Failed to update image');
    }
  };

  const handleCompareImages = () => {
    const n = selectedImageIds.length;
    if (n < 1 || n > MAX_COMPARE_SELECTION) {
      message.warning(
        `Please select 1 to ${MAX_COMPARE_SELECTION} images to compare`
      );
      return;
    }

    const picked = selectedImageIds
      .map(id => images.find((img: ImageItem) => img.id === id))
      .filter((img): img is ImageItem => img != null);
    const imagesToCompare = sortImagesForCompareAscending(picked);
    setSelectedImageIds(imagesToCompare.map(img => img.id));
    setComparingImages(imagesToCompare);
    setSelectedImages(imagesToCompare);
    setCurrentImageIndex(0);
    setViewerModalVisible(true);
  };

  const handleClearSelection = () => {
    setSelectedImageIds([]);
    setCompareMode(false);
  };

  const handleSelectionChange = (selectedIds: number[]) => {
    const capped = selectedIds.slice(0, MAX_COMPARE_SELECTION);
    if (selectedIds.length > MAX_COMPARE_SELECTION) {
      message.info(
        `You can select up to ${MAX_COMPARE_SELECTION} images for comparison`
      );
    }
    setSelectedImageIds(capped);
    if (capped.length === 0) {
      setCompareMode(false);
    }
  };

  const categoryButtons = [
    { key: 'GENERAL', label: 'General' },
    { key: 'PRIVATE', label: 'Private' },
    { key: 'LHR', label: 'LHR' },
  ];

  const filteredImages = images;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Card
        variant="borderless"
        style={{ flex: 1, overflow: 'hidden' }}
        styles={{
          body: {
            padding: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <div
          className="lg:flex-row lg:justify-between lg:items-center lg:gap-0 flex flex-col gap-2"
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div className="lg:flex-none w-full lg:w-auto">
            <Button.Group size="small" className="w-full lg:w-auto">
              {categoryButtons.map(button => (
                <Button
                  key={button.key}
                  type={activeCategory === button.key ? 'primary' : 'default'}
                  onClick={() => setActiveCategory(button.key as ImageCategory)}
                  className="flex-1 lg:flex-none text-xs lg:text-sm"
                >
                  {button.label}
                </Button>
              ))}
            </Button.Group>
          </div>

          <div className="lg:flex-row lg:gap-2 lg:justify-end w-full lg:w-auto flex flex-col gap-1">
            {compareMode && (
              <>
                <div className="text-xs text-gray-500 text-center lg:hidden">
                  {selectedImageIds.length} selected
                </div>
                <span className="hidden lg:inline text-sm text-gray-500">
                  {selectedImageIds.length} selected
                </span>
                <div className="flex gap-2 lg:flex-row">
                  <Button
                    icon={<ClearOutlined />}
                    onClick={handleClearSelection}
                    className="flex-1 lg:flex-none text-xs lg:text-sm"
                    size="small"
                  >
                    <span className="lg:inline hidden">Clear Selection</span>
                    <span className="lg:hidden">Clear</span>
                  </Button>
                  <Tooltip
                    title={
                      selectedImageIds.length < 1 ||
                      selectedImageIds.length > MAX_COMPARE_SELECTION
                        ? `Please select 1 to ${MAX_COMPARE_SELECTION} images to compare`
                        : ''
                    }
                  >
                    <Button
                      type="primary"
                      icon={<ArrowsAltOutlined />}
                      onClick={handleCompareImages}
                      disabled={
                        selectedImageIds.length < 1 ||
                        selectedImageIds.length > MAX_COMPARE_SELECTION
                      }
                      className="flex-1 lg:flex-none text-xs lg:text-sm"
                      size="small"
                    >
                      <span className="lg:inline hidden">
                        Compare Images ({selectedImageIds.length}/
                        {MAX_COMPARE_SELECTION})
                      </span>
                      <span className="lg:hidden">
                        Compare ({selectedImageIds.length}/
                        {MAX_COMPARE_SELECTION})
                      </span>
                    </Button>
                  </Tooltip>
                </div>
              </>
            )}
            {!compareMode && (
              <Button
                onClick={() => setCompareMode(true)}
                className="w-full lg:w-auto text-xs lg:text-sm"
                size="small"
              >
                Select for Compare
              </Button>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setUploadModalVisible(true)}
              className="w-full lg:w-auto text-xs lg:text-sm"
              size="small"
            >
              <span className="lg:inline hidden">Upload Images</span>
              <span className="lg:hidden">Upload Images</span>
            </Button>
          </div>
        </div>

        <div
          className="h-[calc(100vh-390px)] lg:h-[calc(100vh-280px)]"
          style={{
            overflow: 'auto',
            padding: '12px',
          }}
        >
          <Spin
            spinning={loading}
            style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          >
            {filteredImages.length > 0 ? (
              <div
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <ImageGrid
                  images={filteredImages}
                  selectedImages={selectedImageIds}
                  onImageSelect={handleImageSelect}
                  onImageDelete={handleImageDelete}
                  onImageView={handleImageView}
                  onImageUpdate={handleImageUpdate}
                  selectable={compareMode}
                  onSelectionChange={handleSelectionChange}
                  maxCompareSelection={MAX_COMPARE_SELECTION}
                />
              </div>
            ) : (
              <Empty
                description={`No ${activeCategory.toLowerCase()} images found`}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Spin>
        </div>
      </Card>

      <ImageUploadModal
        visible={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        onSuccess={handleUploadSuccess}
        patientId={patientId as number}
        category={activeCategory}
        onUpload={uploadImages}
      />

      <ImageViewerModal
        visible={viewerModalVisible}
        onCancel={() => {
          setViewerModalVisible(false);
          setComparingImages([]);
          setSelectedImages([]);
        }}
        images={comparingImages.length > 0 ? comparingImages : selectedImages}
        currentIndex={currentImageIndex}
        onIndexChange={setCurrentImageIndex}
        onDelete={handleImageDelete}
        compareMode={comparingImages.length > 0}
        selectedForComparison={comparingImages}
      />
    </div>
  );
};
